import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { ViewerAdapter } from '../viewer/adapter.js';
import { setCameraDispatch } from '../viewer/camera-bridge.js';

export interface CameraTarget {
  pos: readonly [number, number, number];
  lookAt: readonly [number, number, number];
  fov: number;
}

export interface FlyToOptions {
  durationMs: number;
  signal?: AbortSignal;
  onTick?: (progress: number) => void;
}

const FOV_MIN = 10;
const FOV_MAX = 170;

// Symmetric ease-in-out cubic. f(0)=0, f(0.5)=0.5, f(1)=1. Monotonic on [0,1].
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function lerp3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

export async function flyToCamera(
  adapter: ViewerAdapter,
  controls: OrbitControls | null,
  from: CameraTarget,
  to: CameraTarget,
  opts: FlyToOptions,
): Promise<void> {
  const clampedToFov = Math.max(FOV_MIN, Math.min(FOV_MAX, to.fov));
  const clampedFromFov = Math.max(FOV_MIN, Math.min(FOV_MAX, from.fov));

  if (opts.durationMs <= 0) {
    setCameraDispatch(adapter, { pos: to.pos, lookAt: to.lookAt, fov: clampedToFov });
    if (controls !== null) {
      controls.target.set(to.lookAt[0], to.lookAt[1], to.lookAt[2]);
      controls.update();
    }
    opts.onTick?.(1);
    return;
  }

  const start = performance.now();
  return new Promise<void>((resolve, reject) => {
    let raf = 0;
    const cleanup = (): void => {
      if (opts.signal !== undefined) {
        opts.signal.removeEventListener('abort', onAbort);
      }
    };
    const onAbort = (): void => {
      cancelAnimationFrame(raf);
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    };
    if (opts.signal !== undefined) {
      if (opts.signal.aborted) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }
    // WS5 PR3.2b (Codex CRITICAL): try/catch dentro RAF. setCamera /
    // controls.update possono throw (es. adapter dispose race, OrbitControls
    // dopo dispose). Senza catch, l'errore RAF e' silent (browser): la
    // Promise non si risolve mai → chiamanti come selectViewbox restano
    // pending → teleportPhase blocked 'gliding' forever. Reject esplicito
    // permette al chiamante di catturare in catch e fare cleanup state.
    const tick = (): void => {
      try {
        const elapsed = performance.now() - start;
        const t = Math.min(1, elapsed / opts.durationMs);
        const e = easeInOutCubic(t);
        const pos = lerp3(from.pos, to.pos, e);
        const look = lerp3(from.lookAt, to.lookAt, e);
        const fov = clampedFromFov + (clampedToFov - clampedFromFov) * e;
        setCameraDispatch(adapter, { pos, lookAt: look, fov });
        if (controls !== null) {
          controls.target.set(look[0], look[1], look[2]);
          controls.update();
        }
        opts.onTick?.(t);
        if (t < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          cleanup();
          resolve();
        }
      } catch (err) {
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    raf = requestAnimationFrame(tick);
  });
}
